import { StyleSheet } from 'react-native';
import { getResponsiveValue } from './breakpoints';
import { colors } from './theme';

export const MediaManagerStyles = StyleSheet.create({
  // Main container
  container: {
    flex: 1,
    height: '100%',
    width: '100%',
    gap: 0,
    fontFamily: 'Glacial Indifference',
    borderRadius: 10,
    overflow: 'hidden',
  },
  containerDragOver: {
    borderColor: '#68AD10',
  },

  // Drag over indicator
  dragOverIndicator: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(74, 124, 10, 0.1)',
    zIndex: 1000,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 10,
  },
  dragOverContent: {
    backgroundColor: '#68AD10',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  dragOverText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
  },

  // Loading overlay
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1001,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 10,
  },
  loadingContent: {
    backgroundColor: 'rgba(61, 127, 230, 0.5)',
    paddingHorizontal: 12,
    paddingVertical: 15,
    borderRadius: 15,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },
  loadingText: {
    color: '#ffffff',
    fontSize: 12,
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
  },

  // Header rows
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 15,
  },
  // On mobile/tablet, stack breadcrumb and search vertically to avoid overlap
  breadcrumbRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
  },
  breadcrumbContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flex: 1,
  },
  backButton: {
    marginRight: 12,
    padding: 6,
    borderRadius: 6,
    backgroundColor: colors.purpleReplacement,
  },
  breadcrumbPath: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  breadcrumbRoot: {
    fontSize: 14,
    color: '#ffffff',
    fontFamily: 'Glacial Indifference',
  },
  breadcrumbSeparator: {
    fontSize: 14,
    color: '#666',
    marginHorizontal: 6,
  },
  breadcrumbItem: {
    fontSize: 14,
    fontFamily: 'Glacial Indifference',
  },
  breadcrumbItemActive: {
    color: '#333',
  },
  breadcrumbItemInactive: {
    color: colors.purpleReplacement,
  },

  // Search bar
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minWidth: 200,
    maxWidth: 300,
  },
  searchContainerFullWidth: {
    maxWidth: '100%',
  },
  searchInput: {
    flex: 1,
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'Glacial Indifference',
  },
  searchClearButton: {
    marginLeft: 8,
  },

  // Action buttons row
  actionButtonsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    marginBottom: 8,
    gap: 8,
    flexWrap: 'wrap',
  },
  actionButton: {
    borderRadius: 20,
    paddingHorizontal: 15,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  actionButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontFamily: 'Glacial Indifference',
    whiteSpace: 'nowrap',
  },
  migrateButton: {
    backgroundColor: 'rgba(255,165,0,0.8)',
  },
  resetButton: {
    backgroundColor: 'rgba(220,53,69,0.9)',
  },
  unprotectButton: {
    backgroundColor: 'rgba(255,193,7,0.9)',
  },
  openButton: {
    borderRadius: 20,
    backgroundColor: colors.purpleReplacement,
    paddingHorizontal: 12,
    paddingVertical: 6,
    gap: 4,
  },
  moveButton: {
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: 8,
  },
  moveButtonDisabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  clearButton: {
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
    padding: 8,
  },
  clearButtonDisabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  newFolderButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    gap: 5,
  },
  addFilesButton: {
    padding: 6,
    borderRadius: 6,
    backgroundColor: '#28a745',
    gap: 4,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },

  // Content area
  contentArea: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 16,
    flex: 1,
    height: '100%',
    gap: 8,
    overflow: 'auto',
  },
  // Single column on tablet and mobile to avoid cutoffs
  contentAreaSingleColumn: {
    flexDirection: 'column',
  },

  // Folder styles
  folderContainer: {
    width: getResponsiveValue({
      mobile: '48%',
      tablet: '31%',
      laptop: '22%',
      desktop: '22%'
    }),
    marginBottom: 15,
    alignItems: 'center',
    position: 'relative',
  },
  folderWrapper: {
    width: '100%',
    height: '100%',
  },
  folderTouchable: {
    width: '100%',
    height: getResponsiveValue({
      mobile: 100,
      tablet: 100,
      laptop: 130,
      desktop: 130
    }),
    padding: 5,
    position: 'relative',
    borderWidth: 2,
    borderColor: 'transparent',
    borderRadius: 12,
  },
  folderTouchableSelected: {
    borderColor: '#ffffff',
  },
  folderTab: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '40%',
    height: 24,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 10,
    zIndex: 0,
  },
  searchMatchIndicator: {
    position: 'absolute',
    top: 2,
    right: 2,
    backgroundColor: '#97ff11',
    borderRadius: 8,
    paddingHorizontal: 4,
    paddingVertical: 2,
    zIndex: 10,
    ...(typeof window !== 'undefined' ? { boxShadow: '0 1px 3px rgba(0,0,0,0.3)' } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.3,
      shadowRadius: 2,
      elevation: 3,
    }),
  },
  folderBody: {
    position: 'absolute',
    top: 10,
    left: 0,
    zIndex: 1,
    right: 0,
    bottom: 0,
    borderRadius: 10,
    borderTopLeftRadius: 0,
    ...(typeof window !== 'undefined' ? { boxShadow: '0 4px 8px rgba(0,0,0,0.2)' } : {
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 4,
      elevation: 3,
    }),
    overflow: 'hidden',
  },
  folderBackgroundImage: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0.7,
  },
  folderGradient: {
    flex: 1,
    borderRadius: 8,
    borderTopLeftRadius: 0,
    padding: 2,
    justifyContent: 'flex-end',
  },
  folderGradientImage: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },
  folderInfo: {
    alignItems: 'center',
    marginTop: 8,
    paddingHorizontal: 4,
    width: '100%'
  },
  folderInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  folderName: {
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 14,
      laptop: 14,
      desktop: 14
    }),
    fontFamily: 'Glacial Indifference',
    textAlign: 'center',
    marginBottom: 2,
    width: '100%',
    color: '#ffffff',
  },
  folderDetails: {
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 11,
      desktop: 11
    }),
    opacity: 0.7,
    color: '#ffffff',
    textAlign: 'center',
  },

  // File content styles
  fileImage: {
    width: '100%',
    height: '100%',
  },
  filePlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  filePlaceholderText: {
    color: '#ffffff',
    fontSize: 10,
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: 4,
    paddingHorizontal: 4,
  },
  fileGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 8,
  },
  fileName: {
    color: '#ffffff',
    fontSize: 10,
    fontFamily: 'Glacial Indifference',
  },

  // Filename label under items
  fileNameContainer: {
    paddingTop: 6,
    paddingHorizontal: 4,
    minHeight: 30,
    justifyContent: 'center',
  },
  fileNameText: {
    fontSize: 12,
    color: '#111111',
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // File type specific colors
  pdfBackground: {
    backgroundColor: '#e74c3c',
  },
  documentBackground: {
    backgroundColor: '#3498db',
  },
  audioBackground: {
    backgroundColor: '#9b59b6',
  },
  videoBackground: {
    backgroundColor: '#e67e22',
  },
  defaultBackground: {
    backgroundColor: '#95a5a6',
  },

  // Empty state
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    opacity: 0.6,
    width: '100%',
  },
  emptyStateText: {
    color: '#ffffff',
    opacity: 0.7,
    fontSize: 16,
    marginTop: 10,
    textAlign: 'center',
  },

  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: 'Glacial Indifference',
    marginBottom: 20,
    textAlign: 'center',
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    marginBottom: 15,
    fontSize: 16,
  },
  modalLabel: {
    fontSize: 14,
    marginBottom: 10,
    color: '#666',
  },
  modalButtonRow: {
    flexDirection: 'row',
    gap: 10,
  },
  modalButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonCancel: {
    backgroundColor: '#f0f0f0',
  },
  modalButtonPrimary: {
    backgroundColor: colors.purpleReplacement,
  },
  modalButtonText: {
    fontSize: 16,
  },
  modalButtonTextPrimary: {
    color: '#ffffff',
    fontFamily: 'Glacial Indifference',
  },

  // Color picker
  colorPickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 20,
  },
  colorPickerItem: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 3,
    borderOpacity: 0.2,
    borderColor: '#aaa',
  },
  colorPickerItemSmall: {
    width: 50,
    height: 50,
    borderRadius: 25,
    borderWidth: 2,
    borderColor: '#ddd',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  colorPickerItemSelected: {
    borderOpacity: 0.5,
    borderColor: '#333',
  },

  // Delete confirmation
  deleteWarning: {
    backgroundColor: '#fff3cd',
    borderRadius: 8,
    padding: 12,
    marginBottom: 15,
    borderLeftWidth: 4,
    borderLeftColor: '#ffc107',
  },
  deleteWarningTitle: {
    fontSize: 14,
    fontFamily: 'Glacial Indifference',
    color: '#856404',
    marginBottom: 5,
    fontWeight: 'bold',
  },
  deleteWarningText: {
    fontSize: 13,
    color: '#856404',
    marginBottom: 2,
  },
  deleteWarningTextLast: {
    fontSize: 13,
    color: '#856404',
  },
  deleteConfirmText: {
    fontSize: 12,
    color: '#dc3545',
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: 15,
  },

  // File delete modal
  deleteFileIcon: {
    alignItems: 'center',
    marginBottom: 20,
  },
  deleteFileTitle: {
    fontSize: 18,
    fontFamily: 'Glacial Indifference',
    marginTop: 10,
    textAlign: 'center',
    color: '#e74c3c',
  },
  deleteFileDescription: {
    fontSize: 16,
    marginBottom: 10,
    textAlign: 'center',
    color: '#333',
    fontFamily: 'Glacial Indifference',
  },
  deleteFileInfo: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: '#e74c3c',
  },
  deleteFileInfoTitle: {
    fontSize: 14,
    fontFamily: 'Glacial Indifference',
    color: '#333',
    fontWeight: 'bold',
  },
  deleteFileInfoSubtext: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  deleteFileWarning: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    marginBottom: 20,
    fontStyle: 'italic',
  },

  // Success/Error modals
  successModal: {
    alignItems: 'center',
  },
  successTitle: {
    fontSize: 18,
    fontFamily: 'Glacial Indifference',
    marginTop: 10,
    marginBottom: 15,
    textAlign: 'center',
    color: '#2ecc71',
  },
  successMessage: {
    fontSize: 14,
    color: '#333',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  successButton: {
    backgroundColor: '#2ecc71',
    borderRadius: 8,
    paddingHorizontal: 30,
    paddingVertical: 12,
    alignItems: 'center',
  },
  successButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
  },

  errorModal: {
    alignItems: 'center',
  },
  errorTitle: {
    fontSize: 18,
    fontFamily: 'Glacial Indifference',
    marginTop: 10,
    marginBottom: 15,
    textAlign: 'center',
    color: '#e74c3c',
  },
  errorMessage: {
    fontSize: 14,
    color: '#333',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  errorButton: {
    backgroundColor: '#e74c3c',
    borderRadius: 8,
    paddingHorizontal: 30,
    paddingVertical: 12,
    alignItems: 'center',
  },
  errorButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
  },

  // Context menu
  contextMenu: {
    position: 'absolute',
    backgroundColor: '#ffffff',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    minWidth: 150,
    overflow: 'hidden',
  },
  contextMenuItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  contextMenuItemLast: {
    borderBottomWidth: 0,
  },
  contextMenuText: {
    fontSize: 14,
    color: '#333',
  },
  contextMenuTextDanger: {
    fontSize: 14,
    color: '#e74c3c',
  },

  // Legacy file styles (for backward compatibility)
  legacyFileContainer: {
    width: getResponsiveValue({
      mobile: '48%',
      tablet: '31%',
      laptop: '22%',
      desktop: '22%'
    }),
    marginBottom: 15,
    minHeight: getResponsiveValue({
      mobile: 120,
      tablet: 120,
      laptop: 180,
      desktop: 180
    }),
    height: '100%',
    alignItems: 'center',
    position: 'relative',
  },
  legacyFileWrapper: {
    width: '100%',
    height: '100%',
  },
  legacyFileTouchable: {
    width: '100%',
    height: getResponsiveValue({
      mobile: 100,
      tablet: 100,
      laptop: 130,
      desktop: 130
    }),
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginBottom: 15,
    position: 'relative',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  legacyFileTouchableSelected: {
    borderColor: '#ffffff',
  },
  legacyFileGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 8,
  },
  legacyFileName: {
    color: '#ffffff',
    fontSize: 10,
    fontFamily: 'Glacial Indifference',
  },

  // Specific buttons
  renameButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  },
  deleteButton: {
    backgroundColor: 'rgba(231, 76, 60, 0.4)'
  },
});

// Predefined folder colors
export const folderColors = [
  '#0bb5df', '#e74c3c', '#2ecc71', '#f39c12',
  '#9b59b6', '#1abc9c', '#34495e', '#e67e22'
];
